import type { ReactNode } from 'react';

import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { QUERY_BIG_QUERY_SYNC_JOBS } from '@/graphql/queries/bigQuerySyncPreview';
import type { BigQuerySyncJobSummaryGql } from '@/graphql/types/bigQuerySyncPreview';
import { bigQuerySyncJobStatusBadgeClass } from '@/services/bigQuerySyncPreview/bigQuerySyncJobStatusBadge';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;
const STATUS_ALL = '__ALL__';

const STATUS_FILTER_OPTIONS = [
  'PENDING',
  'QUEUED',
  'RUNNING',
  'READY',
  'PREVIEW_READY',
  'AWAITING_APPROVAL',
  'COMPLETED',
  'FAILED',
  'APPROVED',
  'APPLIED',
  'REJECTED',
  'CANCELLED',
] as const;

type BigQuerySyncJobsListSectionProps = {
  selectedJobId: string | null;
  onSelectJob: (jobId: string) => void;
  listVersion: number;
  headerAction?: ReactNode;
};

function formatTs(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function BigQuerySyncJobsListSection({
  selectedJobId,
  onSelectJob,
  listVersion,
  headerAction,
}: BigQuerySyncJobsListSectionProps) {
  const [offset, setOffset] = useState(0);
  const [statusValue, setStatusValue] = useState<string>(STATUS_ALL);
  const skipListVersionEffect = useRef(true);

  const variables = useMemo(() => {
    const base: { limit: number; offset: number; status?: string } = {
      limit: PAGE_SIZE,
      offset,
    };
    if (statusValue !== STATUS_ALL) {
      base.status = statusValue;
    }
    return base;
  }, [offset, statusValue]);

  const { data, loading, error, refetch } = useQuery(QUERY_BIG_QUERY_SYNC_JOBS, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const jobs: BigQuerySyncJobSummaryGql[] = data?.bigQuerySyncJobs ?? [];
  const hasPrev = offset > 0;
  const hasNext = jobs.length === PAGE_SIZE;

  useEffect(() => {
    if (skipListVersionEffect.current) {
      skipListVersionEffect.current = false;
      return;
    }
    void refetch();
  }, [listVersion, refetch]);

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    setOffset(0);
  };

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4 shadow-xs">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold">Jobs de sincronização</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Abra um job existente para ver status, diff e aprovar ou rejeitar. A lista é paginada no
            servidor.
          </p>
        </div>
        {headerAction ? (
          <div className="flex shrink-0 justify-end sm:pt-0.5">{headerAction}</div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="grid gap-2">
          <Label htmlFor="bq-jobs-status">Filtrar por status</Label>
          <Select value={statusValue} onValueChange={handleStatusChange}>
            <SelectTrigger id="bq-jobs-status" size="default" className="w-[min(100%,220px)]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={STATUS_ALL}>Todos</SelectItem>
              {STATUS_FILTER_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
          Atualizar lista
        </Button>
      </div>

      {error ? <p className="text-sm text-destructive">{error.message}</p> : null}

      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="min-w-[200px]">ID</TableHead>
              <TableHead>Progresso</TableHead>
              <TableHead className="w-[160px]">Criado em</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : null}
            {!loading && jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">
                  Nenhum job nesta página.
                </TableCell>
              </TableRow>
            ) : null}
            {jobs.map((job) => {
              const active = selectedJobId === job.id;
              return (
                <TableRow
                  key={job.id}
                  className={active ? 'bg-muted/50' : undefined}
                  data-state={active ? 'selected' : undefined}
                >
                  <TableCell className="align-middle">
                    <Badge
                      variant="outline"
                      className={cn('font-mono', bigQuerySyncJobStatusBadgeClass(job.status))}
                    >
                      {job.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-middle font-mono text-xs">{job.id}</TableCell>
                  <TableCell className="max-w-[280px] align-middle text-sm">
                    {job.progress_label ?? '—'}
                    {job.error ? (
                      <span className="mt-1 block truncate text-destructive">{job.error}</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-middle text-xs text-muted-foreground">
                    {formatTs(job.created_at)}
                  </TableCell>
                  <TableCell className="align-middle text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant={active ? 'secondary' : 'default'}
                      onClick={() => onSelectJob(job.id)}
                    >
                      {active ? 'Selecionado' : 'Abrir'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Página {Math.floor(offset / PAGE_SIZE) + 1} · {PAGE_SIZE} por página
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasPrev || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Anterior
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasNext || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Próxima
          </Button>
        </div>
      </div>
    </section>
  );
}
